import { styles as micrStyles } from './micr-processing-view-styles';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { restorePage } from 'src/common/components/emerald-home-page/emerald-home-page';
import { Box, Grid, Typography } from '@material-ui/core';
import { ReactComponent as CheckFront } from 'src/common/media/CheckFront.svg';
import BaseButton from 'src/common/components/base-button';
import { store } from 'src/common/redux/configure-store';
import { ManualEndorseProps } from './manual-endorsement';
import { TenderingModeHomePage } from 'src/home-page/components/home-page-tendering';
import { MICRActions } from 'src/common/redux/reducers/micr-reducer';
import { Document } from 'src/common/interfaces/dtos/IReceipt';
import { getEndorsementReceipts } from 'src/tender-check/business-logic/handle-endorsement-receipts';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { CheckSide } from 'src/devices/micr-utilities';
import { makeEndorsedCheckPayment } from '../business-logic/handle-print-endorsement';

const ManualEndorseMessage: React.FC<ManualEndorseProps> = ({
  screenData,
  onlyShowBack,
}: ManualEndorseProps): JSX.Element => {
  const [formattedReceiptData, setFormattedReceiptData] = useState<(string | undefined)[]>(['']);
  const { t } = useTranslation();
  const [currentSide, setCurrentSide] = useState<CheckSide>(CheckSide.Front);
  const [processPayment, setProcessPayment] = useState<boolean>(false);
  const { endorsementConfig, tenderDetails } = screenData;

  useEffect(() => {
    withSpinner(async () => {
      const receipts = await getEndorsementReceipts(tenderDetails);
      const checkBack = receipts[0]?.documents[1];
      checkBack && setFormattedReceiptData(formatReceiptData(checkBack));
    });
  }, [tenderDetails, setFormattedReceiptData]);

  useMemo(() => {
    if (
      (endorsementConfig.receiptLinesFront &&
        endorsementConfig.receiptLinesFront.length > 0 &&
        !endorsementConfig.receiptLinesBack) ||
      endorsementConfig.receiptLinesBack?.length === 0
    ) {
      setProcessPayment(true);
    } else if (
      (endorsementConfig.receiptLinesBack &&
        endorsementConfig.receiptLinesBack.length > 0 &&
        (!endorsementConfig.receiptLinesFront ||
          endorsementConfig.receiptLinesFront.length === 0)) ||
      onlyShowBack === true
    ) {
      setProcessPayment(true);
      setCurrentSide(CheckSide.Back);
    } else {
      if (currentSide === CheckSide.Back) {
        setProcessPayment(true);
      }
    }
  }, [
    setCurrentSide,
    currentSide,
    endorsementConfig.receiptLinesBack,
    endorsementConfig.receiptLinesFront,
    onlyShowBack,
  ]);

  const micrClasses = micrStyles();

  const handleClick = async (): Promise<void> => {
    if (processPayment) {
      await makeEndorsedCheckPayment(tenderDetails);
    } else {
      setCurrentSide(CheckSide.Back);
    }
  };

  return (
    <Grid container md={7} direction='column' className={micrClasses.manualEntryCenterPage}>
      <Grid
        item
        container
        direction='column'
        data-testid='manual-endorse-message-container'
        className={micrClasses.manualEndorseMessageGridContainer}
      >
        <Grid
          item
          data-testid='manual-endorse-title'
          className={micrClasses.checkScreenMessageTitleGrid}
        >
          <Typography variant='h2'>{t('ManualEndorsementTitle')}</Typography>
        </Grid>
        <Grid item className={`${micrClasses.checkScreenDividerGrid}`}>
          <div
            data-testid='manual-endorse-title-bar'
            className={`${micrClasses.paymentTitleBar}`}
          ></div>
        </Grid>
        <Grid
          item
          data-testid='manual-endorse-message'
          className={`${micrClasses.checkScreenMessageBodyGrid} ${micrClasses.greyColor}`}
        >
          {currentSide === CheckSide.Front ? (
            <Typography variant='body1'>{t('ManualEndorsementFrontMessage')}</Typography>
          ) : (
            <Typography variant='body1'>{t('ManualEndorsementBackMessage')}</Typography>
          )}
        </Grid>
        <Grid item>
          {currentSide === CheckSide.Front ? (
            <CheckFront data-testid='front-check-image' style={{ marginLeft: '0.5vw' }} />
          ) : (
            <CheckBack formattedReceiptData={formattedReceiptData} />
          )}
        </Grid>
      </Grid>
      <Grid container className={micrClasses.checkScreenBottomButtons}>
        <Grid item container direction='row' className={micrClasses.manualEntryButtonContainers}>
          <BaseButton
            color='inherit'
            buttonType='white'
            className={`${micrClasses.containedButton} ${micrClasses.bottomButton}`}
            onClick={() => closeManualCheckEndorsementView()}
            data-testid='manual-endorse-cancel'
          >
            <Typography variant='button'>{t('Cancel')}</Typography>
          </BaseButton>
          <BaseButton
            color='secondary'
            className={`${micrClasses.containedButton} ${micrClasses.bottomButton}`}
            onClick={() => handleClick()}
            data-testid='manual-endorse-next-or-submit'
          >
            {processPayment !== true ? (
              <Typography variant='button'>{t('Next')}</Typography>
            ) : (
              <Typography variant='button'>{t('Done')}</Typography>
            )}
          </BaseButton>
        </Grid>
      </Grid>
    </Grid>
  );
};

const CheckBack: React.FC<CheckBackProps> = ({
  formattedReceiptData,
}: CheckBackProps): JSX.Element => {
  const micrClasses = micrStyles();
  return (
    <Grid container className={micrClasses.boxContainer} data-testid='back-check-info'>
      <Box className={micrClasses.box}>
        {formattedReceiptData.map((text, i) => (
          <Typography
            key={i}
            variant='body2'
            className={`${micrClasses.greyColor} ${micrClasses.textPositions}`}
          >
            {text}
          </Typography>
        ))}
      </Box>
    </Grid>
  );
};

const formatReceiptData = (receiptBack: Document) => {
  const { commands } = receiptBack;
  return commands?.filter(obj => obj.text !== '').map(obj => obj.text);
};

const closeManualCheckEndorsementView = (): void => {
  store.dispatch(MICRActions.resetMICRData());
  restorePage(TenderingModeHomePage);
};

interface CheckBackProps {
  formattedReceiptData: (string | undefined)[];
}

export default ManualEndorseMessage;
